import type { api } from '@udm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart3, ClipboardList, Eye, FileDown, FolderKanban, Lightbulb } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, NavLink, useNavigate, useParams } from 'react-router';
import { canAny } from '../../api/client';
import {
  deleteReport,
  fetchLightPoints,
  fetchReportOverview,
  fetchReports,
  reportFileUrl,
} from '../../api/ops';
import { BarChart, LineChart } from '../../ui/charts';
import { Card, Kpi, PageHeader } from '../../ui/components';
import { formatDateTime, formatNumber } from '../../ui/format';
import { useMe } from '../auth/session';
import { GenerateReport } from './GenerateReport';
import { ProjectsTab } from './ProjectsTab';
import { ReportScopePicker, useReportScope } from './ReportScope';

const TABS = [
  { key: 'dashboard', icon: BarChart3 },
  { key: 'reports', icon: ClipboardList },
  { key: 'light-points', icon: Lightbulb },
  { key: 'projects', icon: FolderKanban },
] as const;

/** Reports / AFM (requirements c, e, i, j): dashboard, stored reports, per light point table, projects & baselines. */
export function ReportsPage() {
  const { t } = useTranslation();
  const { tab = 'dashboard' } = useParams();
  if (!TABS.some((x) => x.key === tab)) return <Navigate to="/reports/dashboard" replace />;
  return (
    <div className="page">
      <PageHeader title={t('reportsPage.title')} actions={<ReportScopePicker />} />
      <nav className="tabs" aria-label={t('reportsPage.title')}>
        {TABS.map((x) => (
          <NavLink key={x.key} to={`/reports/${x.key}`}>
            <x.icon size={15} />
            {t(`reportsPage.tabs.${x.key}`)}
          </NavLink>
        ))}
      </nav>
      {tab === 'dashboard' && <Dashboard />}
      {tab === 'reports' && <ReportsList />}
      {tab === 'light-points' && <LightPoints />}
      {tab === 'projects' && <ProjectsTab />}
    </div>
  );
}

const kwh = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${formatNumber(v, v < 100 ? 1 : 0)} kWh`;
const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : `${formatNumber(v, 1)} %`;

function EnergyBlock({ e, title }: { e: api.EnergySavings; title: string }) {
  const { t } = useTranslation();
  const b = e.baseline;
  return (
    <Card title={title}>
      <div className="kpi-row">
        <Kpi label={t('afm.measured')} value={kwh(e.measured_kwh)} />
        <Kpi
          label={t('afm.coverage')}
          value={
            <>
              {pct(e.coverage * 100)}
              <small>{t('afm.coverageHint', { n: e.devices_with_energy, total: e.devices_total })}</small>
            </>
          }
        />
        {b?.status === 'ok' ? (
          <>
            <Kpi
              label={t('afm.savings')}
              value={pct(b.savings_pct)}
              tone={b.meets_minimum ? 'online' : 'critical'}
            />
            <Kpi label={t('afm.savingsKwh')} value={kwh(b.savings_kwh)} />
            <Kpi label={t('afm.co2')} value={`${formatNumber(b.co2_avoided_t ?? 0, 2)} t`} />
          </>
        ) : (
          <Kpi label={t('afm.savings')} value={b ? t('afm.insufficient') : t('afm.noBaseline')} />
        )}
      </div>
      <dl className="kv" style={{ marginTop: 'var(--space-3)' }}>
        <dt>{t('afm.period')}</dt>
        <dd>
          {e.from} — {e.to}
          {e.data_from && e.data_from > e.from && (
            <span className="cell-sub">{t('afm.dataFrom', { day: e.data_from })}</span>
          )}
        </dd>
        {b && (
          <>
            <dt>{t('afm.baseline')}</dt>
            <dd>
              {kwh(b.annual_kwh)} / {t('afm.year')} · {b.source_document} ({b.reference_date})
              <span className="cell-sub">
                {t('afm.baselinePeriod', { value: kwh(b.period_kwh) })} ·{' '}
                {t('afm.estimatedTotal', { value: kwh(e.estimated_total_kwh) })} ·{' '}
                {t('afm.minimum', { value: pct(b.min_savings_pct) })}
              </span>
            </dd>
          </>
        )}
        <dt>{t('afm.dimming')}</dt>
        <dd>
          {pct(e.dimming.savings_pct)} ({kwh(e.dimming.savings_kwh)})
          <span className="cell-sub">{t('afm.dimmingHint')}</span>
        </dd>
      </dl>
      <BarChart
        ariaLabel={t('afm.monthly')}
        data={e.monthly.map((m) => ({
          label: m.month.slice(2),
          value: m.measured_kwh,
          ref: m.baseline_kwh,
          title: `${m.month}: ${kwh(m.measured_kwh)}${m.baseline_kwh != null ? ` / ${t('afm.baseline')} ${kwh(m.baseline_kwh)}` : ''} · ${pct(m.coverage * 100)}`,
        }))}
        format={(v) => formatNumber(v)}
      />
    </Card>
  );
}

function Dashboard() {
  const { t } = useTranslation();
  const { clientId, projectId } = useReportScope();
  const me = useMe().data;
  const o = useQuery({
    queryKey: ['reports', 'overview', clientId, projectId],
    queryFn: () => fetchReportOverview(clientId!, projectId),
    enabled: !!clientId,
  });
  const f = o.data?.functioning;
  if (!clientId) return <p className="muted">{t('reportsPage.pickClient')}</p>;
  return (
    <div className="ops-stack">
      {canAny(me, 'report.generate') && <GenerateReport clientId={clientId} projectId={projectId} />}
      {o.data && f && (
        <>
          <Card title={t('afm.functioning')}>
            <div className="kpi-row">
              <Kpi label={t('afm.installed')} value={formatNumber(f.total)} />
              <Kpi label={t('afm.seen24h')} value={formatNumber(f.seen_24h)} tone="online" />
              <Kpi label={t('afm.seen7d')} value={formatNumber(f.seen_7d)} />
              <Kpi label={t('afm.never')} value={formatNumber(f.never)} />
              <Kpi label={t('afm.availability')} value={pct(f.availability_pct)} />
              {/* null for the authority observer: it sees no alarms */}
              {f.active_alarms !== null && (
                <Kpi
                  label={t('afm.activeAlarms')}
                  value={`${formatNumber(f.active_alarms)} / ${formatNumber(f.active_critical ?? 0)}`}
                  tone="critical"
                />
              )}
              <Kpi label={t('afm.hoursLifetime')} value={formatNumber(f.hours_avg_lifetime ?? undefined)} />
              <Kpi label={t('afm.hoursNight')} value={formatNumber(f.hours_avg_night ?? undefined, 1)} />
            </div>
            <p className="muted small">
              {f.by_protocol.map((x) => `${x.protocol}: ${formatNumber(x.n)}`).join(' · ')} ·{' '}
              {t('afm.withGps', { n: formatNumber(f.with_gps) })}
              {f.alarms_opened !== null &&
                ` · ${t('afm.alarms30d', { opened: f.alarms_opened, resolved: f.alarms_resolved })}`}
            </p>
            <h4>{t('afm.availabilityTrend')}</h4>
            <LineChart
              ariaLabel={t('afm.availabilityTrend')}
              max={100}
              points={o.data.availability_daily.map((d) => ({ label: d.day.slice(5), value: d.pct }))}
              format={(v) => `${Math.round(v)}%`}
            />
          </Card>
          {o.data.energy_project && (
            <EnergyBlock
              e={o.data.energy_project}
              title={t('afm.energyProject', { name: o.data.project?.name ?? '' })}
            />
          )}
          <EnergyBlock e={o.data.energy_ytd} title={t('afm.energyYtd')} />
          <Card title={t('reportsPage.last')} bodyClass="">
            <ReportTable items={o.data.last_reports} />
          </Card>
        </>
      )}
    </div>
  );
}

function ReportTable({ items, canDelete = false }: { items: api.Report[]; canDelete?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: deleteReport,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reports'] }),
  });
  const navigate = useNavigate();
  // the authority observer ("AFM") opens a stored report as the page itself, PDF only; other roles get the file links
  const portal = !canAny(useMe().data, 'operations.view');
  return (
    <div className="ops-scroll">
      <table className="table table-wrap">
        <thead>
          <tr>
            <th>{t('reportsPage.type')}</th>
            <th>{t('reportsPage.scope')}</th>
            <th>{t('afm.period')}</th>
            <th>{t('reportsPage.generatedAt')}</th>
            <th>{t('common.status')}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id}>
              <td>
                {t(`reportsPage.types.${r.type}`)}
                <span className="cell-sub">
                  {r.trigger === 'automatic' ? t('reportsPage.automatic') : (r.generated_by ?? '—')}
                </span>
              </td>
              <td>
                {r.client_name}
                {(r.project_name ?? r.site_name) && (
                  <span className="cell-sub">{r.project_name ?? r.site_name}</span>
                )}
              </td>
              <td className="nowrap">{r.period_from ? `${r.period_from} — ${r.period_to}` : '—'}</td>
              <td className="muted nowrap">{formatDateTime(r.created_at)}</td>
              <td>
                <span
                  className={`pill${r.status === 'ready' ? ' pill-online' : r.status === 'failed' ? ' pill-critical' : ''}`}
                >
                  {t(`reportsPage.status.${r.status}`)}
                </span>
              </td>
              <td className="right nowrap">
                {portal && r.formats.includes('pdf') && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void navigate(`/reports/view/${r.id}`)}
                  >
                    <Eye size={15} /> {t('reportsPage.viewer.open')}
                  </button>
                )}
                {!portal &&
                  r.formats.map((f) => (
                    <a
                      key={f}
                      className="btn btn-ghost"
                      href={reportFileUrl(r.id, f)}
                      download
                      title={f.toUpperCase()}
                    >
                      <FileDown size={14} /> {f.toUpperCase()}
                    </a>
                  ))}
                {canDelete && (
                  <button type="button" className="btn btn-ghost btn-danger" onClick={() => del.mutate(r.id)}>
                    {t('common.delete')}
                  </button>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="muted">
                {t('reportsPage.none')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ReportsList() {
  const { t } = useTranslation();
  const me = useMe().data;
  const { clientId, projectId } = useReportScope();
  const [type, setType] = useState('');
  const list = useQuery({
    queryKey: ['reports', 'list', clientId, type],
    queryFn: () => fetchReports(clientId, type || undefined),
    enabled: !!clientId,
  });
  return (
    <div className="ops-stack">
      {canAny(me, 'report.generate') && <GenerateReport clientId={clientId} projectId={projectId} />}
      <Card
        title={t('reportsPage.stored')}
        bodyClass=""
        actions={
          <select
            className="input"
            value={type}
            onChange={(e) => setType(e.target.value)}
            aria-label={t('reportsPage.type')}
          >
            <option value="">{t('common.all')}</option>
            {(
              [
                'functioning',
                'energy_annual',
                'light_points',
                'monitoring_annual',
                'installation_sheet',
                'inventory',
              ] as const
            ).map((x) => (
              <option key={x} value={x}>
                {t(`reportsPage.types.${x}`)}
              </option>
            ))}
          </select>
        }
      >
        <ReportTable items={list.data?.items ?? []} canDelete={canAny(me, 'report.manage')} />
      </Card>
    </div>
  );
}

function LightPoints() {
  const { t } = useTranslation();
  const { clientId, projectId } = useReportScope();
  const [q, setQ] = useState('');
  const data = useQuery({
    queryKey: ['reports', 'light-points', clientId, projectId],
    queryFn: () => fetchLightPoints(clientId!, projectId),
    enabled: !!clientId,
  });
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data.data?.items ?? []).filter(
      (r) =>
        !s ||
        r.name.toLowerCase().includes(s) ||
        (r.serial ?? '').toLowerCase().includes(s) ||
        (r.site ?? '').toLowerCase().includes(s),
    );
  }, [data.data, q]);
  const csv = () => {
    const cols: [string, (r: api.LightPointList['items'][number]) => unknown][] = [
      [t('reportsPage.lp.name'), (r) => r.name],
      [t('common.site'), (r) => r.site],
      [t('reportsPage.lp.serial'), (r) => r.serial],
      ['lat', (r) => r.lat],
      ['lon', (r) => r.lon],
      [t('reportsPage.lp.hours'), (r) => r.hours_lifetime],
      [t('reportsPage.lp.hoursNight'), (r) => r.hours_avg_night],
      [t('reportsPage.lp.dimNow'), (r) => r.dimmer_pct],
      [t('reportsPage.lp.dimProgrammed'), (r) => r.programmed_pct],
      [t('reportsPage.lp.energy'), (r) => r.energy_lifetime_kwh],
      [t('reportsPage.lp.power'), (r) => r.power_w],
      [t('reportsPage.lp.measuredAt'), (r) => r.measured_at],
    ];
    const esc = (v: unknown) => {
      const s = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = [
      cols.map((c) => esc(c[0])).join(','),
      ...rows.map((r) => cols.map((c) => esc(c[1](r))).join(',')),
    ].join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8' }));
    a.download = 'light-points.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Card
      title={t('reportsPage.lp.title', { n: rows.length })}
      bodyClass=""
      actions={
        <span className="toolbar">
          <input
            className="input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('common.search')}
            aria-label={t('common.search')}
          />
          <button type="button" className="btn" onClick={csv} disabled={!rows.length}>
            <FileDown size={15} /> CSV
          </button>
        </span>
      }
    >
      <div className="card-body muted small" style={{ paddingBottom: 0 }}>
        {t('reportsPage.lp.hint', { avg: formatNumber(data.data?.avg_hours_lifetime ?? undefined) })}
      </div>
      <div className="ops-scroll">
        <table className="table table-tight">
          <thead>
            <tr>
              <th>{t('reportsPage.lp.name')}</th>
              <th>{t('reportsPage.lp.gps')}</th>
              <th className="right">{t('reportsPage.lp.hours')}</th>
              <th className="right">{t('reportsPage.lp.hoursNight')}</th>
              <th className="right">{t('reportsPage.lp.dimNow')}</th>
              <th className="right">{t('reportsPage.lp.dimProgrammed')}</th>
              <th className="right">{t('reportsPage.lp.energy')}</th>
              <th className="right">{t('reportsPage.lp.power')}</th>
              <th>{t('reportsPage.lp.measuredAt')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 2000).map((r) => (
              <tr key={r.id}>
                <td>
                  <Link to={`/devices/${r.id}`}>{r.name}</Link>
                  <span className="cell-sub">
                    {r.site}
                    {r.group ? ` · ${r.group}` : ''}
                  </span>
                </td>
                <td className="mono small">
                  {r.lat !== null ? `${r.lat.toFixed(5)}, ${r.lon?.toFixed(5)}` : '—'}
                </td>
                <td className="num">{formatNumber(r.hours_lifetime ?? undefined)}</td>
                <td className="num">{formatNumber(r.hours_avg_night ?? undefined, 1)}</td>
                <td className="num">{r.dimmer_pct ?? '—'}</td>
                <td className="num">{r.programmed_pct ?? '—'}</td>
                <td className="num">{formatNumber(r.energy_lifetime_kwh ?? undefined, 1)}</td>
                <td className="num">{formatNumber(r.power_w ?? undefined, 1)}</td>
                <td className="muted small nowrap">{r.measured_at ? formatDateTime(r.measured_at) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > 2000 && <p className="card-body muted">{t('reportsPage.lp.truncated')}</p>}
    </Card>
  );
}
